import React, { useEffect, useRef, useCallback } from 'react';
import { FiX } from 'react-icons/fi';
import Icon from '../Icon';

type ModalProps = {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide';
    headerActions?: React.ReactNode;
    /** e.g. z-[60] when stacking modals */
    overlayClassName?: string;
    /** Override default scrollable body wrapper */
    bodyClassName?: string;
    /** When false, clicking the dimmed backdrop does not close the dialog. */
    closeOnBackdropClick?: boolean;
};

export default function Modal({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    headerActions,
    overlayClassName = '',
    bodyClassName,
    closeOnBackdropClick = true,
}: ModalProps) {
    const bodyRef = useRef<HTMLDivElement>(null);
    const scrollActiveTimerRef = useRef<number | null>(null);

    const onBodyScroll = useCallback(() => {
        const el = bodyRef.current;
        if (!el) return;
        el.classList.add('modal-scroll--active');
        if (scrollActiveTimerRef.current != null) {
            window.clearTimeout(scrollActiveTimerRef.current);
        }
        scrollActiveTimerRef.current = window.setTimeout(() => {
            scrollActiveTimerRef.current = null;
            el.classList.remove('modal-scroll--active');
        }, 700);
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen && bodyRef.current) {
            bodyRef.current.classList.remove('modal-scroll--active');
        }
        return () => {
            if (scrollActiveTimerRef.current != null) {
                window.clearTimeout(scrollActiveTimerRef.current);
            }
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!closeOnBackdropClick) return;
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    /* 60% viewport width from sm up; full width on very small screens */
    const sizeClasses = {
        sm: 'w-full max-w-md sm:w-[60%] sm:max-w-md',
        md: 'w-full max-w-lg sm:w-[60%] sm:max-w-lg',
        lg: 'w-full max-w-2xl sm:w-[60%] sm:max-w-2xl',
        xl: 'w-full max-w-4xl sm:w-[60%] sm:max-w-4xl',
        wide: 'w-full max-w-full sm:w-[60%]',
    };

    return (
        <div
            className={`fixed inset-0 z-50 flex min-h-screen items-center justify-center bg-black/50 p-4 backdrop-blur-sm ${overlayClassName}`.trim()}
            onClick={handleOverlayClick}
        >
            <div
                className={`flex min-w-0 ${sizeClasses[size]} max-h-[90vh] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                {title && (
                    <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700 sm:px-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {title}
                        </h2>
                        <div className="flex items-center gap-2">
                            {headerActions}
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                                aria-label="Close"
                            >
                                <Icon icon={FiX} className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                )}
                <div
                    ref={bodyRef}
                    onScroll={onBodyScroll}
                    className={
                        bodyClassName ??
                        'modal-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-white dark:bg-gray-800'
                    }
                >
                    {children}
                </div>
            </div>
        </div>
    );
}
